import { Kbd, Pill, PillsInput, type PillsInputFieldProps } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import React, { useEffect, useState } from 'react';

// The native Mantine input is funky with key capture (especially with Esscape), so we write our own
const KeyCaptureInput = React.forwardRef<
  HTMLInputElement,
  {
    onKeyCapture?: (keys: string[]) => void;
    label?: string;
    className?: string;
    inputProps?: PillsInputFieldProps;
  }
>(({ onKeyCapture, label, className, inputProps }, ref) => {
  const [actionValue, setActionValue] = useState<string[]>([]);
  const [focused, setFocused] = useState(false);

  // Global keyboard listener, required since other this app won't let me capture Escape key pressed
  useEffect(() => {
    if (!focused) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const modifiers = [
        e.ctrlKey ? 'Control' : undefined,
        e.altKey ? 'Alt' : undefined,
        e.shiftKey ? 'Shift' : undefined,
        e.metaKey ? 'Meta' : undefined,
      ].filter((x) => x !== undefined);

      const keyPress = (modifiers.includes(e.key) ? modifiers : [...modifiers, e.key])
        // Transform keys (like space)
        .map((x) => (x === ' ' ? 'Space' : x));
      setActionValue(keyPress);
      onKeyCapture?.(keyPress);

      // Prevent default behavior
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onKeyCapture, focused]);

  const clickOutsideRef = useClickOutside(() => setFocused(false));

  return (
    <PillsInput
      label={label}
      className={`caret-transparent ${className ?? ''}`}
      onFocus={() => setFocused(true)}
      // Cannot use onBlur because it messes with 'Escape' key capture
      ref={clickOutsideRef}
    >
      <Pill.Group className="flex-nowrap whitespace-nowrap">
        {actionValue.map((key, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-gray-500">+</span>}
            <Kbd>{key}</Kbd>
          </React.Fragment>
        ))}
        <PillsInput.Field
          ref={ref}
          placeholder={actionValue.length === 0 ? 'Press any key/combo' : ''}
          type="hidden"
          {...inputProps}
        />
        {/* This just keeps the height from changing */}
        <Kbd className="invisible">+</Kbd>
      </Pill.Group>
    </PillsInput>
  );
});

KeyCaptureInput.displayName = 'KeyCaptureInput';

export default KeyCaptureInput;
